/** Inline banner for success / error / warning / info messages. */

import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import type { ReactNode } from 'react';

type Tone = 'success' | 'error' | 'warning' | 'info';

const TONES = {
  success: {
    box: 'bg-green-50 border-green-200 text-green-800 dark:bg-green-500/10 dark:border-green-500/30 dark:text-green-200',
    Icon: CheckCircleIcon,
  },
  error: {
    box: 'bg-red-50 border-red-200 text-red-800 dark:bg-red-500/10 dark:border-red-500/30 dark:text-red-200',
    Icon: XCircleIcon,
  },
  warning: {
    box: 'bg-amber-50 border-amber-200 text-amber-800 dark:bg-amber-500/10 dark:border-amber-500/30 dark:text-amber-200',
    Icon: ExclamationTriangleIcon,
  },
  info: {
    box: 'bg-primary-50 border-primary-200 text-primary-800 dark:bg-primary-500/10 dark:border-primary-500/30 dark:text-primary-200',
    Icon: InformationCircleIcon,
  },
} as const;

export function Alert({
  tone = 'info',
  title,
  children,
}: {
  tone?: Tone;
  title?: ReactNode;
  children?: ReactNode;
}) {
  const { box, Icon } = TONES[tone];
  return (
    <div className={`flex gap-3 rounded-lg border p-4 text-sm ${box}`} role="alert">
      <Icon className="h-5 w-5 shrink-0" aria-hidden="true" />
      <div className="min-w-0 space-y-1">
        {title ? <p className="font-semibold">{title}</p> : null}
        {children ? <div className="break-words">{children}</div> : null}
      </div>
    </div>
  );
}
